import { useEffect, useRef } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import type { Editor, Extensions } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Mention from '@tiptap/extension-mention'
import { Bold, Code, Italic, Link2, List, ListOrdered, Strikethrough } from 'lucide-react'
import { Tooltip } from '../ds'
import { buildMentionSuggestion } from '../editor/mentionSuggestion'

// Плавающая панель форматирования (появляется при выделении текста) — стиль Notion.
function BubbleToolbar({ editor }: { editor: Editor }) {
  function setLink() {
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Адрес ссылки', prev || 'https://')
    if (url === null) return
    const chain = editor.chain().focus().extendMarkRange('link')
    if (url.trim() === '') chain.unsetLink().run()
    else chain.setLink({ href: url.trim() }).run()
  }
  function btn(active: boolean, onClick: () => void, label: string, icon: React.ReactNode) {
    return (
      <button
        type="button"
        className={`fmt-btn ${active ? 'is-active' : ''}`}
        // mousedown preventDefault — чтобы не терять выделение редактора.
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
        title={label}
        aria-label={label}
      >
        {icon}
      </button>
    )
  }
  return (
    <>
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'Жирный', <Bold size={15} />)}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'Курсив', <Italic size={15} />)}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), 'Зачёркнутый', <Strikethrough size={15} />)}
      {btn(editor.isActive('code'), () => editor.chain().focus().toggleCode().run(), 'Моноширинный', <Code size={15} />)}
      <span className="fmt-sep" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), 'Список', <List size={15} />)}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), 'Нумерованный список', <ListOrdered size={15} />)}
      <span className="fmt-sep" />
      {btn(editor.isActive('link'), setLink, 'Ссылка', <Link2 size={15} />)}
    </>
  )
}

// Rich-text редактор на TipTap, стилизованный классами дизайн-системы
// (.rich.is-editor + .composer-box). Контролируемый по value.
// - members → включается @mention с выпадашкой участников.
// - toolbar → фиксированная панель сверху (для черновика).
// - bubble → плавающая панель форматирования при выделении (для комментариев).
// - onSubmit → вызов по Cmd/Ctrl+Enter.
export function RichEditor({
  value,
  onChange,
  placeholder = 'Текст комментария…',
  members,
  toolbar = true,
  bubble = false,
  onReady,
  onSubmit
}: {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  members?: { email: string }[]
  toolbar?: boolean
  bubble?: boolean
  onReady?: (editor: Editor) => void
  onSubmit?: () => void
}) {
  // Актуальный список участников для suggestion (редактор создаётся один раз).
  const membersRef = useRef(members ?? [])
  useEffect(() => {
    membersRef.current = members ?? []
  }, [members])

  // onSubmit через ref — чтобы handleKeyDown всегда видел свежий колбэк.
  const onSubmitRef = useRef(onSubmit)
  useEffect(() => {
    onSubmitRef.current = onSubmit
  }, [onSubmit])

  const extensions: Extensions = [
    StarterKit,
    Link.configure({ openOnClick: false, autolink: true }),
    Placeholder.configure({ placeholder })
  ]
  if (members) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: buildMentionSuggestion(() => membersRef.current.map((member) => ({ id: member.email, label: member.email })))
      })
    )
  }

  const editor = useEditor({
    extensions,
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    editorProps: {
      attributes: { class: 'rich is-editor rich-editor' },
      handleKeyDown(_view, event) {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && onSubmitRef.current) {
          event.preventDefault()
          onSubmitRef.current()
          return true
        }
        return false
      }
    }
  })

  // Внешнее обновление (например, после ИИ-генерации) — синхронизируем контент.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '')
    }
  }, [value, editor])

  // Отдаём инстанс редактора наружу (для внешних действий, напр. кнопки «@»).
  useEffect(() => {
    if (editor) onReady?.(editor)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])

  if (!editor) {
    return null
  }

  const tool = (active: boolean): React.CSSProperties => ({ color: active ? 'var(--blue)' : undefined })

  return (
    <div className="composer-box">
      {bubble && (
        <BubbleMenu editor={editor} className="bubble-menu">
          <BubbleToolbar editor={editor} />
        </BubbleMenu>
      )}
      {toolbar && (
        <div className="row" style={{ padding: '2px 4px', gap: 2, borderBottom: '1px solid var(--border)' }}>
          <Tooltip content="Жирный">
            <button
              type="button"
              className="icon-btn"
              style={tool(editor.isActive('bold'))}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold size={15} />
            </button>
          </Tooltip>
          <Tooltip content="Курсив">
            <button
              type="button"
              className="icon-btn"
              style={tool(editor.isActive('italic'))}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic size={15} />
            </button>
          </Tooltip>
          <Tooltip content="Список">
            <button
              type="button"
              className="icon-btn"
              style={tool(editor.isActive('bulletList'))}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <List size={15} />
            </button>
          </Tooltip>
          <Tooltip content="Ссылка">
            <button
              type="button"
              className="icon-btn"
              style={tool(editor.isActive('link'))}
              onClick={() => {
                const prev = editor.getAttributes('link').href as string | undefined
                const url = window.prompt('Адрес ссылки', prev || 'https://')
                if (url === null) return
                if (url === '') {
                  editor.chain().focus().unsetLink().run()
                } else {
                  editor.chain().focus().setLink({ href: url }).run()
                }
              }}
            >
              <Link2 size={15} />
            </button>
          </Tooltip>
        </div>
      )}
      <div className="composer-input">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
